import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import ReadinessBadge from "../components/ReadinessBadge";
import { 
  ArrowRight, FileText, Calendar, Loader2, Lock, 
  TrendingUp, TrendingDown, CheckCircle2, AlertTriangle,
  Target, Sparkles, Download
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FullAssessmentResultsPage() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    fetchResults();
    fetchPricing();
  }, [assessmentId]);

  // Separate effect for Pro status that depends on user being available
  useEffect(() => {
    if (user) {
      fetchProStatus();
    }
  }, [user]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/assessment/results/${assessmentId}`, {
        headers: getAuthHeaders()
      });
      setResults(response.data);
    } catch (error) {
      toast.error("Failed to load results");
      navigate("/assessment");
    } finally {
      setLoading(false);
    }
  };

  const fetchProStatus = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/user/pro-status`, {
        headers: getAuthHeaders()
      });
      setIsPro(response.data.is_pro);
    } catch (error) {
      console.error("Failed to fetch pro status:", error);
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/payment/pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error("Failed to fetch pricing:", error);
    }
  };

  const handleCheckout = async (priceType) => {
    if (!user) {
      toast.error("Please sign in to continue");
      navigate(`/auth?redirect=/assessment/results/${assessmentId}`);
      return;
    }

    setCheckingOut(true);
    try {
      const response = await axios.post(
        `${API}/payment/checkout`,
        {
          price_type: priceType,
          assessment_id: assessmentId,
          origin_url: window.location.origin
        },
        { headers: getAuthHeaders() }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Failed to start checkout");
      setCheckingOut(false);
    }
  };

  const handleGeneratePlan = async (useAI = false) => {
    try {
      const response = await axios.post(
        `${API}/business-plan/generate`,
        { 
          assessment_id: assessmentId,
          use_ai: useAI && isPro  // Only use AI if Pro user
        },
        { headers: getAuthHeaders() }
      );
      navigate(`/business-plan/${response.data.id}`);
    } catch (error) {
      toast.error("Failed to generate business plan");
    }
  };

  const handleCreateActionPlan = async () => {
    try {
      const response = await axios.post(
        `${API}/action-plan/create`,
        { assessment_id: assessmentId },
        { headers: getAuthHeaders() }
      );
      navigate(`/action-plan/${response.data.id}`);
    } catch (error) {
      toast.error("Failed to create action plan");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
        <p className="text-muted-foreground">Calculating your FoundrScore...</p>
      </div>
    );
  }

  const sectionScores = results?.section_details || {};

  return (
    <div className="py-8 md:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Your FoundrScore Results</h1>
          <p className="text-muted-foreground">
            Full assessment complete • {results?.business_name || "Your Business Idea"}
          </p>
        </div>

        {/* Main Score Badge */}
        <div className="mb-10" data-testid="foundrscore-badge">
          <ReadinessBadge 
            level={results?.readiness_level} 
            percentage={results?.total_score}
            size="lg"
          />
        </div>

        {/* Section Breakdown */}
        <Card className="mb-8" data-testid="section-breakdown-card">
          <CardHeader>
            <CardTitle className="text-lg">Section Breakdown</CardTitle>
            <CardDescription>
              Weighted scores across 8 areas (evidence questions count 2×)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(sectionScores).map(([sectionId, section]) => {
                const isWeak = results?.weakest_sections?.some(w => w.id === sectionId);
                const isStrong = results?.strongest_sections?.some(s => s.id === sectionId);
                
                return (
                  <div key={sectionId} className="space-y-2" data-testid={`section-${sectionId}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{section.title}</span>
                        {isWeak && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                            Needs work
                          </span>
                        )}
                        {isStrong && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                            Strong
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {section.weighted_score}/{section.weight} ({section.percentage}%)
                      </span>
                    </div>
                    <Progress 
                      value={section.percentage} 
                      className={`h-2 ${isWeak ? '[&>div]:bg-red-500' : isStrong ? '[&>div]:bg-emerald-500' : ''}`}
                    />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Strengths & Weaknesses */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Strongest */}
          <Card data-testid="strongest-sections-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Your Strongest Sections
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {results?.strongest_sections?.map((section, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">{section.title}</p>
                      <p className="text-xs text-muted-foreground">
                        Score: {section.score}/{section.weight} ({section.percentage}%)
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Weakest */}
          <Card data-testid="weakest-sections-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingDown className="w-5 h-5 text-red-500" />
                Priority Improvements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {results?.weakest_sections?.map((section, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">{section.title}</p>
                      <p className="text-xs text-muted-foreground">
                        Score: {section.score}/{section.weight} ({section.percentage}%)
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Next Steps */}
        <Card className="mb-8" data-testid="next-steps-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="w-5 h-5 text-primary" />
              Your Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {results?.next_steps?.map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </span>
                  <span className="text-sm pt-0.5">{step}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Payment CTA / Pro Features */}
        {!isPro ? (
          <Card className="mb-8 border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5" data-testid="upgrade-cta">
            <CardContent className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Unlock Your Full Potential</h3>
                  <p className="text-sm text-muted-foreground">
                    Get your tailored business plan, validation canvas, and 30-day action plan
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {pricing?.options?.map((option) => (
                  <div 
                    key={option.id}
                    className={`p-4 rounded-lg border-2 ${option.recommended ? 'border-primary bg-white' : 'border-muted bg-muted/30'}`}
                  >
                    {option.recommended && (
                      <span className="text-xs font-semibold text-primary uppercase">Recommended</span>
                    )}
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-2xl font-bold">{option.price_formatted}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{option.description}</p>
                    <ul className="space-y-1 mb-4">
                      {option.features.slice(0, 3).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className="w-full rounded-full"
                      variant={option.recommended ? "default" : "outline"}
                      onClick={() => handleCheckout(option.id)}
                      disabled={checkingOut}
                      data-testid={`checkout-${option.id}`}
                    >
                      {checkingOut ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          {option.recommended ? "Get Started" : "Choose Plan"}
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>

              <p className="text-xs text-center text-muted-foreground">
                Secure payment via Stripe. Cancel anytime for monthly plans.
              </p>
            </CardContent>
          </Card>
        ) : (
          /* Pro user - show action buttons */
          <Card className="mb-8 border-emerald-200 bg-emerald-50" data-testid="pro-actions">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-emerald-800">Pro Features Unlocked</span>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                <Button 
                  onClick={() => handleGeneratePlan(true)}
                  className="rounded-full"
                  data-testid="generate-ai-plan-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  AI Plan
                </Button>
                <Button 
                  onClick={() => handleGeneratePlan(false)}
                  variant="outline"
                  className="rounded-full"
                  data-testid="generate-plan-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Basic Plan
                </Button>
                <Button 
                  onClick={handleCreateActionPlan}
                  variant="outline"
                  className="rounded-full"
                  data-testid="create-action-plan-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  30-Day Plan
                </Button>
                <Link to={`/canvas?assessment=${assessmentId}`}>
                  <Button variant="outline" className="w-full rounded-full" data-testid="prefill-canvas-btn">
                    <Target className="w-4 h-4 mr-2" />
                    Pre-fill Canvas
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Free Business Plan CTA */}
        <Card className="mb-8" data-testid="free-plan-cta">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Free One-Page Business Plan</h3>
                  <p className="text-sm text-muted-foreground">
                    View your rule-based plan pulled from your assessment answers
                    {!isPro && " (PDF download requires Pro)"}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline"
                onClick={handleGeneratePlan}
                className="rounded-full whitespace-nowrap"
                data-testid="view-free-plan-btn"
              >
                View Plan
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <p className="text-xs text-center text-muted-foreground max-w-lg mx-auto">
          Your FoundrScore reflects your assessment answers, not market guarantees. 
          Always validate with real customers. This is guidance, not financial advice.
        </p>
      </div>
    </div>
  );
}
